import { useState, useEffect } from "react";
import { Play, Plus, Youtube, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: "", description: "", youtube_url: "" });
  const [addingVideo, setAddingVideo] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.title || !newVideo.youtube_url) {
      toast.error("Please fill in required fields");
      return;
    }

    setAddingVideo(true);
    try {
      const response = await axios.post(`${API}/videos`, newVideo);
      setVideos([response.data, ...videos]);
      setNewVideo({ title: "", description: "", youtube_url: "" });
      setAddDialogOpen(false);
      toast.success("Video added successfully!");
    } catch (error) {
      console.error("Error adding video:", error);
      toast.error("Failed to add video");
    } finally {
      setAddingVideo(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;
    
    try {
      await axios.delete(`${API}/videos/${videoId}`);
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success("Video deleted");
    } catch (error) {
      console.error("Error deleting video:", error);
      toast.error("Failed to delete video");
    }
  };

  return (
    <div data-testid="videos-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Media Gallery
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
                Voices Against Violence
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl">
                Watch powerful stories and messages from our community. These videos showcase our advocacy work and the impact we're making together.
              </p>
            </div>
            <div className="flex gap-3">
              <a 
                href="https://www.youtube.com/@voicesagainstviolencecoali2074" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="btn-secondary">
                  <Youtube className="w-5 h-5 mr-2 text-red-500" />
                  YouTube Channel
                </Button>
              </a>
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-video-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit']">Add New Video</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddVideo} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="video-title">Title *</Label>
                      <Input
                        id="video-title"
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Enter video title"
                        className="mt-1"
                        required
                        data-testid="video-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="video-url">YouTube URL *</Label>
                      <Input
                        id="video-url"
                        value={newVideo.youtube_url}
                        onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="mt-1"
                        required
                        data-testid="video-url-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="video-description">Description</Label>
                      <Textarea
                        id="video-description"
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description of the video"
                        className="mt-1 resize-none"
                        rows={3}
                        data-testid="video-description-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={addingVideo}
                      data-testid="submit-video-btn"
                    >
                      {addingVideo ? "Adding..." : "Add Video"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner w-10 h-10" />
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <div 
                  key={video.id} 
                  className="card-video group"
                  data-testid={`video-card-${video.id}`}
                >
                  <div 
                    className="relative aspect-video bg-slate-200 cursor-pointer"
                    onClick={() => setSelectedVideo(video)}
                  >
                    <img 
                      src={video.thumbnail_url} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/640x360?text=Video+Thumbnail";
                      }}
                    />
                    <div className="video-overlay">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#0056D2] ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#0F172A] mb-2 line-clamp-2">{video.title}</h3>
                        {video.description && (
                          <p className="text-sm text-slate-500 line-clamp-2">{video.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        data-testid={`delete-video-${video.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F0F9FF] rounded-3xl">
              <Youtube className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Videos Yet</h3>
              <p className="text-slate-500 mb-6">Add videos from YouTube to showcase your work.</p>
              <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
                <Plus className="w-5 h-5 mr-2" />
                Add First Video
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div>
              <div className="youtube-container">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">{selectedVideo.title}</h3>
                {selectedVideo.description && (
                  <p className="text-slate-600">{selectedVideo.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
